import { useState } from 'react'
import {
  useChecklistTemplate,
  useChecklistTemplates,
  useCloneChecklistTemplate,
  useDeleteChecklistTemplate,
  useUpdateChecklistTemplate,
  type ChecklistTemplate,
  type ChecklistTemplateParameter,
  type ChecklistTemplateSummary,
  type TemplateDataType,
} from '../api/templates'
import { errorMessage } from '../lib/format'
import { isPeriod } from '../lib/templates'

const DEAL_TYPE_LABEL: Record<string, string> = {
  MnA: 'M&A',
  CapitalRaise: 'Capital raise',
  DebtFinancing: 'Debt financing',
  Custom: 'Custom',
}

/**
 * The organization's request-list templates (ADR 0010): the system's, read-only, and its own.
 * What a template ASKS is written in a room and saved from there ("Save as a template" on a
 * room's checklist), because the editor for sections and requests lives there. This page is for
 * what a room cannot say: the name, the deal type, whether it is offered, and what each
 * {{token}} means. The API decides who may do any of it.
 */
export function ChecklistTemplatesPanel({ organizationId }: { organizationId: string }) {
  const templates = useChecklistTemplates(organizationId, { includeInactive: true })
  const clone = useCloneChecklistTemplate(organizationId)
  const remove = useDeleteChecklistTemplate(organizationId)
  const [editing, setEditing] = useState<string | null>(null)
  const [copying, setCopying] = useState<ChecklistTemplateSummary | null>(null)
  const [copyName, setCopyName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const fail = (e: unknown) => setError(errorMessage(e))

  // An API older than this page has no such route: say nothing rather than show an error on the policy page.
  if (templates.isPending || templates.isError) return null

  return (
    <section className="panel">
      <h2 className="h2">Request-list templates</h2>
      <p className="small muted">
        A room’s checklist can be built from any of these. To change what a template asks, build a
        list from it in a room, edit it there, and use “Save as a template”. Rooms already built
        keep what they were built with.
      </p>
      {error && <p className="error-text small">{error}</p>}
      <table className="table">
        <thead>
          <tr>
            <th scope="col">Template</th>
            <th scope="col">Kind</th>
            <th scope="col">Deal type</th>
            <th scope="col">Requests</th>
            <th scope="col" />
          </tr>
        </thead>
        <tbody>
          {templates.data.map((t) => (
            <tr key={t.id}>
              <td>
                {t.name}
                {!t.isActive && <span className="small muted"> (not offered)</span>}
                {t.description && <div className="small muted">{t.description}</div>}
              </td>
              <td>
                {t.isSystem ? 'Built in' : 'Yours'}
                {t.isOverlay ? ', overlay' : ''}
              </td>
              <td>{DEAL_TYPE_LABEL[t.dealType] ?? t.dealType}</td>
              <td>
                {t.itemCount} in {t.sectionCount} workstream{t.sectionCount === 1 ? '' : 's'}
                {t.parameterCount > 0 && `, asks ${t.parameterCount}`}
              </td>
              <td>
                <div className="row-actions">
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => {
                      setError(null)
                      setCopying(t)
                      setCopyName(`${t.name} (copy)`)
                    }}
                  >
                    Copy
                  </button>
                  {!t.isSystem && (
                    <>
                      <button type="button" className="btn btn-sm" onClick={() => setEditing(t.id)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        disabled={remove.isPending}
                        onClick={() => {
                          setError(null)
                          if (window.confirm(`Remove the template “${t.name}”?`)) {
                            remove.mutate(t.id, { onError: fail })
                          }
                        }}
                      >
                        Remove
                      </button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {copying && (
        <form
          className="form"
          aria-label="Copy a template"
          onSubmit={(e) => {
            e.preventDefault()
            setError(null)
            clone.mutate(
              { templateId: copying.id, name: copyName.trim() },
              { onSuccess: () => setCopying(null), onError: fail },
            )
          }}
        >
          <label className="field">
            <span>Name of your copy of “{copying.name}”</span>
            <input
              value={copyName}
              maxLength={200}
              required
              onChange={(e) => setCopyName(e.target.value)}
            />
          </label>
          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={clone.isPending || !copyName.trim()}
            >
              {clone.isPending ? 'Copying…' : 'Copy'}
            </button>
            <button type="button" className="btn" onClick={() => setCopying(null)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {editing && (
        <TemplateEditorLoader
          key={editing}
          organizationId={organizationId}
          templateId={editing}
          onDone={() => setEditing(null)}
        />
      )}
    </section>
  )
}

function TemplateEditorLoader({
  organizationId,
  templateId,
  onDone,
}: {
  organizationId: string
  templateId: string
  onDone: () => void
}) {
  const template = useChecklistTemplate(organizationId, templateId)
  if (template.isPending) return <p className="muted small">Loading…</p>
  if (template.isError) return <p className="error-text small">{errorMessage(template.error)}</p>
  return <TemplateEditor organizationId={organizationId} template={template.data} onDone={onDone} />
}

/**
 * Every token the text uses gets a row, declared or not: what is not described is asked for by its bare name.
 * Periods excepted: the build fills them from its own date, and the API refuses to have one declared.
 */
function startingParameters(template: ChecklistTemplate): ChecklistTemplateParameter[] {
  const undeclared = template.tokens.filter(
    (t) => !isPeriod(t) && !template.parameters.some((p) => p.token === t),
  )
  return [
    ...template.parameters,
    ...undeclared.map((token) => ({
      token,
      label: token,
      dataType: 'Text' as const,
      isRequired: true,
    })),
  ]
}

function TemplateEditor({
  organizationId,
  template,
  onDone,
}: {
  organizationId: string
  template: ChecklistTemplate
  onDone: () => void
}) {
  const update = useUpdateChecklistTemplate(organizationId)
  const [name, setName] = useState(template.name)
  const [description, setDescription] = useState(template.description ?? '')
  const [dealType, setDealType] = useState(template.dealType)
  const [industryTag, setIndustryTag] = useState(template.industryTag ?? '')
  const [isActive, setIsActive] = useState(template.isActive)
  const [parameters, setParameters] = useState(() => startingParameters(template))
  const setParameter = (token: string, change: Partial<ChecklistTemplateParameter>) =>
    setParameters((now) => now.map((p) => (p.token === token ? { ...p, ...change } : p)))

  return (
    <form
      className="form form-wide"
      aria-label={`Edit ${template.name}`}
      onSubmit={(e) => {
        e.preventDefault()
        update.mutate(
          {
            templateId: template.id,
            name: name.trim(),
            description: description.trim() || null,
            dealType,
            industryTag: industryTag.trim() || null,
            isActive,
            parameters: parameters.map((p) => ({ ...p, label: p.label.trim() || p.token })),
          },
          { onSuccess: onDone },
        )
      }}
    >
      <label className="field">
        <span>Name</span>
        <input value={name} maxLength={200} required onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="field">
        <span>Description (optional)</span>
        <input
          value={description}
          maxLength={1000}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>
      <label className="field">
        <span>Deal type</span>
        <select value={dealType} onChange={(e) => setDealType(e.target.value)}>
          {Object.entries(DEAL_TYPE_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Industry (optional)</span>
        <input
          value={industryTag}
          maxLength={100}
          onChange={(e) => setIndustryTag(e.target.value)}
        />
      </label>
      <label className="field-inline">
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        <span>Offered when a list is built</span>
      </label>

      {parameters.length > 0 && (
        <fieldset className="checklist-picker">
          <legend className="small">What a build asks for</legend>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">In the text</th>
                <th scope="col">Asked as</th>
                <th scope="col">Kind</th>
              </tr>
            </thead>
            <tbody>
              {parameters.map((p) => (
                <tr key={p.token}>
                  <td>
                    <code>{`{{${p.token}}}`}</code>
                    {!template.tokens.includes(p.token) && (
                      <span className="small muted"> (not used)</span>
                    )}
                  </td>
                  <td>
                    <input
                      aria-label={`Label for ${p.token}`}
                      value={p.label}
                      maxLength={200}
                      onChange={(e) => setParameter(p.token, { label: e.target.value })}
                    />
                  </td>
                  <td>
                    <select
                      aria-label={`Kind of ${p.token}`}
                      value={p.dataType}
                      onChange={(e) =>
                        setParameter(p.token, { dataType: e.target.value as TemplateDataType })
                      }
                    >
                      <option value="Text">Text</option>
                      <option value="Date">Date</option>
                      <option value="Year">Year</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </fieldset>
      )}

      <p className="small muted no-margin">
        {template.itemCount} requests in {template.sections.map((s) => s.name).join(', ')}. Revision{' '}
        {template.revision}.
      </p>
      {update.error && <p className="error-text small">{errorMessage(update.error)}</p>}
      <div className="form-actions">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={update.isPending || !name.trim()}
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  )
}
